import { Percent, Plus, Edit2, Trash2, Loader2, Calendar, Users, Tag, ArrowLeft } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { usePermissions } from '../../hooks/usePermissions';
import { getCurrentOnboardingSection } from '../../utils/onboardingCheck';

interface DiscountCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  duration_type: string;
  duration_months: number | null;
  max_redemptions: number | null;
  expires_at: string | null;
  created_at: string | null;
  is_active: boolean;
}

export default function DiscountCodes() {
  const navigate = useNavigate();
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: companyId, role, isLoading: isLoadingCompany } = useCurrentCompany();
  const permissions = usePermissions();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);
  
  // Check if user is in onboarding flow
  const currentOnboardingSection = getCurrentOnboardingSection();
  const isInOnboarding = currentOnboardingSection === 'subscriptions';

  useEffect(() => {
    const loadData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }

      // Wait for company context to load
      if (isLoadingCompany) {
        return;
      }

      // Wait for permissions to load
      if (permissions.isLoading) {
        return;
      }

      // Check if user has a company context
      if (!companyId) {
        navigate('/company-selector');
        return;
      }

      // Route guard ensures STAFF (owner, admin, coach, reception) can access.
      // Use role from useCurrentCompany to avoid redirect race with usePermissions.
      const staffRoles = ['owner', 'admin', 'coach', 'reception'];
      if (!role || !staffRoles.includes(role)) {
        navigate('/dashboard?error=access_denied');
        return;
      }

      // Load discount codes
      const { data: codes } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      setDiscountCodes(codes || []);
      setLoading(false);
    };

    loadData();
  }, [navigate, companyId, role, isLoadingCompany, permissions.isLoading]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;

    const { error } = await supabase
      .from('discount_codes')
      .delete()
      .eq('id', id);

    if (error) {
      alert('Failed to delete discount code');
      return;
    }

    setDiscountCodes(prev => prev.filter(c => c.id !== id));
  };

  const toggleActive = async (code: DiscountCode) => {
    const { error } = await supabase
      .from('discount_codes')
      .update({ is_active: !code.is_active })
      .eq('id', code.id);

    if (error) {
      alert('Failed to update discount code');
      return;
    }

    setDiscountCodes(prev =>
      prev.map(c => (c.id === code.id ? { ...c, is_active: !c.is_active } : c))
    );
  };

  const formatDiscount = (code: DiscountCode) => {
    if (code.discount_type === 'percent') {
      return `${code.discount_value}% off`;
    }
    return `$${code.discount_value.toFixed(2)} off`;
  };

  const formatDuration = (code: DiscountCode) => {
    if (code.duration_type === 'forever') return 'Forever';
    if (code.duration_type === 'once') return 'First payment only';
    if (code.duration_type === 'repeating' && code.duration_months) {
      return `${code.duration_months} month${code.duration_months > 1 ? 's' : ''}`;
    }
    return code.duration_type;
  };

  const isExpired = (code: DiscountCode) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  if (loading || permissions.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] w-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-5rem)] flex flex-col pt-6">
        {/* Onboarding Banner */}
        {isInOnboarding && (
          <div className="mb-4 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <Tag size={20} className="text-text-primary" />
              <div>
                <p className="text-sm font-medium text-[#EBFFEE]">Setting up your gym</p>
                <p className="text-xs text-[#A9A3A3]">Create discount codes to continue onboarding</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/onboarding')}
              className="flex items-center gap-2 px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white font-medium rounded-lg transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to Onboarding
            </button>
          </div>
        )}

        {/* Add Code - compact top-right when user can manage and has discount codes */}
        {permissions.canManageDiscountCodes && discountCodes.length > 0 && (
          <div className="flex justify-end mb-4 shrink-0">
            <button
              onClick={() => {
                setEditingCode(null);
                setShowCreateForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Code
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-h-0">
        {discountCodes.length === 0 && !showCreateForm ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
            <div className="w-20 h-20 rounded-full bg-[#363232] flex items-center justify-center mx-auto mb-4">
              <Tag size={40} className="text-[#504A4A]" />
            </div>
            <h2 className="text-xl font-semibold text-[#EBFFEE] mb-2">No Discount Codes Yet</h2>
            <p className="text-[#A9A3A3] mb-6">Create discount codes to offer promotions to your members</p>
            {/* Only show create button if user can manage discount codes */}
            {permissions.canManageDiscountCodes && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
              >
                <Plus size={18} />
                Create First Discount Code
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {discountCodes.map((code) => (
              <div
                key={code.id}
                className={`bg-[#221F1F] rounded-xl border p-5 transition-colors ${
                  code.is_active && !isExpired(code)
                    ? 'border-[#504A4A] hover:border-[#9B1421]/50'
                    : 'border-[#504A4A]/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Tag size={16} className="text-rollops-accent" />
                      <h3 className="font-mono font-bold text-[#EBFFEE] text-lg">{code.code}</h3>
                    </div>
                    {code.description && (
                      <p className="text-sm text-[#A9A3A3] mt-1">{code.description}</p>
                    )}
                  </div>
                  {/* Only show edit/delete buttons if user can manage discount codes */}
                  {permissions.canManageDiscountCodes && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditingCode(code);
                          setShowCreateForm(true);
                        }}
                        className="p-2 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#504A4A] rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Percent size={16} className="text-green-500" />
                    <span className="text-[#EBFFEE] font-medium">{formatDiscount(code)}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-[#A9A3A3]" />
                    <span className="text-sm text-[#A9A3A3]">{formatDuration(code)}</span>
                  </div>

                  {code.max_redemptions && (
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-[#A9A3A3]" />
                      <span className="text-sm text-[#A9A3A3]">
                        Max {code.max_redemptions} uses
                      </span>
                    </div>
                  )}

                  {code.expires_at && (
                    <div className={`text-sm ${isExpired(code) ? 'text-red-400' : 'text-[#A9A3A3]'}`}>
                      {isExpired(code) ? 'Expired' : `Expires: ${new Date(code.expires_at).toLocaleDateString()}`}
                    </div>
                  )}
                </div>

                {/* Active Toggle */}
                <button
                  onClick={() => toggleActive(code)}
                  className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                    code.is_active
                      ? 'bg-green-600/20 text-green-500 hover:bg-green-600/30'
                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                  }`}
                >
                  {code.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            ))}
          </div>
        )}

          {/* Create/Edit Form Modal - Only show if user can manage discount codes */}
          {showCreateForm && companyId && permissions.canManageDiscountCodes && (
            <DiscountCodeForm
              companyId={companyId}
              discountCode={editingCode}
              onClose={() => {
                setShowCreateForm(false);
                setEditingCode(null);
              }}
              onSave={(code) => {
                if (editingCode) {
                  setDiscountCodes(prev => prev.map(c => c.id === code.id ? code : c));
                } else {
                  setDiscountCodes(prev => [code, ...prev]);
                }
                setShowCreateForm(false);
                setEditingCode(null);
              }}
            />
          )}
        </div>
      </div>
  );
}

// Discount Code Form Component
interface DiscountCodeFormProps {
  companyId: string;
  discountCode: DiscountCode | null;
  onClose: () => void;
  onSave: (code: DiscountCode) => void;
}

function DiscountCodeForm({ companyId, discountCode, onClose, onSave }: DiscountCodeFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: discountCode?.code || '',
    description: discountCode?.description || '',
    discountType: discountCode?.discount_type || 'percent',
    discountValue: discountCode?.discount_value?.toString() || '',
    duration: discountCode?.duration_type || 'forever',
    durationMonths: discountCode?.duration_months?.toString() || '',
    maxRedemptions: discountCode?.max_redemptions?.toString() || '',
    expiresAt: discountCode?.expires_at 
      ? new Date(discountCode.expires_at).toISOString().split('T')[0] 
      : '',
  });

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, code });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }
    if (!form.discountValue) {
      setError('Discount value is required');
      return;
    }

    setSaving(true);
    setError(null);

    type DiscountCodeData = {
      company_id: string;
      code: string;
      description: string | null;
      discount_type: string;
      discount_value: number;
      duration_type: string;
      duration_months: number | null;
      max_redemptions: number | null;
      expires_at: string | null;
      is_active: boolean;
    };
    const data: DiscountCodeData = {
      company_id: companyId,
      code: form.code.trim().toUpperCase(),
      description: form.description.trim() || null,
      discount_type: form.discountType,
      discount_value: parseFloat(form.discountValue) || 0,
      duration_type: form.duration,
      duration_months: form.duration === 'repeating' ? parseInt(form.durationMonths) || null : null,
      max_redemptions: form.maxRedemptions ? parseInt(form.maxRedemptions) : null,
      expires_at: form.expiresAt || null,
      is_active: true,
    };

    try {
      if (discountCode) {
        // Update
        const { data: updated, error: updateError } = await supabase
          .from('discount_codes')
          .update(data)
          .eq('id', discountCode.id)
          .select()
          .single();

        if (updateError) throw updateError;
        onSave(updated);
      } else {
        // Create
        const { data: created, error: createError } = await supabase
          .from('discount_codes')
          .insert(data)
          .select()
          .single();

        if (createError) throw createError;
        onSave(created);
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'code' in err && err.code === '23505') {
        setError('This code already exists');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save discount code');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] rounded-2xl border border-[#504A4A] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#504A4A]">
          <h2 className="text-xl font-bold text-[#EBFFEE]">
            {discountCode ? 'Edit Discount Code' : 'Create Discount Code'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Code <span className="text-rollops-accent">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g., WELCOME20"
                className="flex-1 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] font-mono uppercase placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                required
              />
              <button
                type="button"
                onClick={generateCode}
                className="px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-[#A9A3A3] rounded-lg transition-colors text-sm"
              >
                Generate
              </button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Description
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g., New member discount"
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Discount Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, discountType: 'percent' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.discountType === 'percent'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Percentage (%)
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, discountType: 'fixed' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.discountType === 'fixed'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Fixed Amount ($)
              </button>
            </div>
          </div>

          {/* Discount Value */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Discount Value <span className="text-rollops-accent">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">
                {form.discountType === 'fixed' ? '$' : ''}
              </span>
              <input
                type="number"
                step={form.discountType === 'percent' ? '1' : '0.01'}
                max={form.discountType === 'percent' ? '100' : undefined}
                value={form.discountValue}
                onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                placeholder={form.discountType === 'percent' ? '20' : '10.00'}
                className={`w-full py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 ${
                  form.discountType === 'fixed' ? 'pl-8 pr-4' : 'px-4'
                }`}
                required
              />
              {form.discountType === 'percent' && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">%</span>
              )}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Duration
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: 'forever' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.duration === 'forever'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Forever
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: 'repeating' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.duration === 'repeating'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Repeating
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, duration: 'once' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.duration === 'once'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Once
              </button>
            </div>
            {form.duration === 'repeating' && (
              <input
                type="number"
                min="1"
                value={form.durationMonths}
                onChange={(e) => setForm({ ...form, durationMonths: e.target.value })}
                placeholder="Number of months"
                className="w-full mt-2 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              />
            )}
          </div>

          {/* Max Redemptions */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Max Redemptions (optional)
            </label>
            <input
              type="number"
              min="1"
              value={form.maxRedemptions}
              onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
              placeholder="Leave empty for unlimited"
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Expiration Date (optional)
            </label>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                discountCode ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

